import { TabNavigationProps, TabProps } from '@cbhq/cds-common';
import { zIndex } from '@cbhq/cds-common/tokens/zIndex';
import { Box, Divider } from '@cbhq/cds-web/layout';
import { TabNavigation } from '@cbhq/cds-web/tabs';

import { useRoutedTabs } from ':cloud/hooks/useRoutedTabs';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';

/**
 * Represents a tab with routing information.
 */
interface RoutedTab extends TabProps {
  /** Route path for the tab */
  route: string;
}

/**
 * Props for the RoutedTopNavTabs component.
 */
interface RoutedTopNavTabsProps extends Omit<TabNavigationProps, 'tabs' | 'value' | 'onChange'> {
  /** Array of RoutedTab objects */
  tabs: RoutedTab[];
  /** Optional boolean to maintain projectId in URL params (defaults to true) */
  preserveProjectId?: boolean;
}

/**
 * Renders a top navigation bar with tabs that change the URL when clicked.
 * Assumes that the component is rendered within route that exists in tabs.
 *
 * @param props.tabs - An array of RoutedTab objects defining the tabs and their routes
 * @param props.preserveProjectId - Optional boolean to maintain projectId in URL params (defaults to true)
 * @returns A React component that displays routed top navigation tabs
 */
export function RoutedTopNavTabs({ tabs, preserveProjectId = true }: RoutedTopNavTabsProps) {
  const { currentTab, handleChange } = useRoutedTabs(tabs, preserveProjectId);

  const { isPhone } = useSimpleBreakpoints();

  if (isPhone) {
    return (
      <TabNavigation
        value={currentTab.id}
        tabs={tabs}
        onChange={handleChange}
        spacingHorizontal={5}
        borderedBottom
        // We need to set the z-index to 0 on mobile so the tabs don't overlap the mobile nav drawer.
        zIndex={0}
      />
    );
  }
  return (
    <>
      {/* Hide the topnav border with a 1px Box */}
      <Box
        spacingStart={1}
        width="100%"
        position="relative"
        top="-1px"
        height={1}
        background="background"
        zIndex={zIndex.navigation}
      />
      <TabNavigation
        value={currentTab.id}
        tabs={tabs}
        onChange={handleChange}
        spacingHorizontal={7}
      />
      <Divider />
    </>
  );
}
